// Drizzle schema for Cloudflare D1 (SQLite). Mirrors the data model in v0.1-spec.md.
// Every user-scoped table carries pack_id — see CLAUDE.md "Foundational decisions":
// the third pack arrives as a TS union extension, not a column migration.

import { sql } from 'drizzle-orm'
import { index, integer, primaryKey, real, sqliteTable, text } from 'drizzle-orm/sqlite-core'

const nowMs = sql`(unixepoch() * 1000)`

export const users = sqliteTable('users', {
  id: text('id').primaryKey(),
  // Nullable: kids using the short-URL flow never set a username.
  username: text('username').unique(),
  displayName: text('display_name').notNull(),
  role: text('role', { enum: ['student', 'teacher'] })
    .notNull()
    .default('student'),
  classCode: text('class_code'),
  churchName: text('church_name'),
  // Token used by the personal short URL `/k/:token`. Step 3 issues it.
  shortToken: text('short_token').unique(),
  // Streak is user-level, not pack-level — Step 10 spec line 678 calls for
  // a single shared streak across packs. lastActivityDate is 'YYYY-MM-DD'
  // in the user's device-local timezone (sent on every answer).
  currentStreak: integer('current_streak').notNull().default(0),
  longestStreak: integer('longest_streak').notNull().default(0),
  lastActivityDate: text('last_activity_date'),
  createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
  // Cohort tag for Step 16 retention analysis. Set on quick-signup from
  // Referer header; seed/classcode flows tag their own rows. Nullable
  // because pre-migration rows have no recoverable origin.
  signupSource: text('signup_source'),
  // Remembered grace companion (practice-loop-v2, migration 0007). Null =
  // not picked yet; validity enforced by companionIdSchema at the API layer.
  companionId: text('companion_id'),
})

export const topics = sqliteTable(
  'topics',
  {
    id: text('id').primaryKey(),
    packId: text('pack_id').notNull(),
    nameZh: text('name_zh').notNull(),
    nameEn: text('name_en').notNull(),
    scriptureRef: text('scripture_ref').notNull(),
    descriptionZh: text('description_zh'),
    descriptionEn: text('description_en'),
    displayOrder: integer('display_order').notNull(),
  },
  (t) => [index('topics_pack_idx').on(t.packId)],
)

export const items = sqliteTable(
  'items',
  {
    id: text('id').primaryKey(),
    packId: text('pack_id').notNull(),
    topicId: text('topic_id')
      .notNull()
      .references(() => topics.id),
    nameZh: text('name_zh').notNull(),
    nameEn: text('name_en').notNull(),
    // Pack scene coordinates. Nullable until Step 8 lays out the scenes.
    sceneX: real('scene_x'),
    sceneY: real('scene_y'),
    sceneWidth: real('scene_width'),
    sceneHeight: real('scene_height'),
    scriptureRef: text('scripture_ref').notNull(),
  },
  (t) => [index('items_pack_idx').on(t.packId), index('items_topic_idx').on(t.topicId)],
)

export const bonusScenes = sqliteTable('bonus_scenes', {
  id: text('id').primaryKey(),
  // One bonus per pack in v0.1 — unique on packId.
  packId: text('pack_id').notNull().unique(),
  nameZh: text('name_zh').notNull(),
  nameEn: text('name_en').notNull(),
  descriptionZh: text('description_zh'),
  scriptureRef: text('scripture_ref').notNull(),
  unlockCondition: text('unlock_condition').notNull(),
})

export const questions = sqliteTable(
  'questions',
  {
    id: text('id').primaryKey(),
    packId: text('pack_id').notNull(),
    topicId: text('topic_id')
      .notNull()
      .references(() => topics.id),
    // v0.1 has two types after dropping `match`. See future-ideas.md for the cut.
    questionType: text('question_type', { enum: ['mcq', 'image_id'] }).notNull(),
    promptZh: text('prompt_zh').notNull(),
    promptEn: text('prompt_en').notNull(),
    // Type-specific data: { options_zh, options_en, correct_index, scripture_ref, image_url? }
    payload: text('payload', { mode: 'json' }).notNull(),
    difficulty: integer('difficulty').notNull().default(1),
    variantGroup: text('variant_group').notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
  },
  (t) => [
    index('questions_pack_topic_idx').on(t.packId, t.topicId),
    index('questions_variant_group_idx').on(t.variantGroup),
  ],
)

export const userItems = sqliteTable(
  'user_items',
  {
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    itemId: text('item_id')
      .notNull()
      .references(() => items.id),
    packId: text('pack_id').notNull(),
    unlockedAt: integer('unlocked_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
    // Round-1 alpha feedback: ItemDetailPage's "影片故事 vs 彩繪填圖" tab default.
    // NULL = first visit (defaults to 影片故事 tab). Once set, subsequent visits
    // default to 彩繪填圖 tab. Frontend PATCHes this on mount once; the endpoint
    // COALESCEs so a second PATCH is a no-op.
    detailSeenAt: integer('detail_seen_at', { mode: 'timestamp_ms' }),
  },
  (t) => [
    primaryKey({ columns: [t.userId, t.itemId] }),
    index('user_items_user_pack_idx').on(t.userId, t.packId),
  ],
)

export const userArtworks = sqliteTable(
  'user_artworks',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    itemId: text('item_id')
      .notNull()
      .references(() => items.id),
    packId: text('pack_id').notNull(),
    artworkR2Key: text('artwork_r2_key').notNull(),
    thumbnailR2Key: text('thumbnail_r2_key'),
    isComplete: integer('is_complete', { mode: 'boolean' }).notNull().default(false),
    // Spec completion rule: ≥5 distinct fill actions OR explicit "完成" click.
    fillActionCount: integer('fill_action_count').notNull().default(0),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
    completedAt: integer('completed_at', { mode: 'timestamp_ms' }),
  },
  (t) => [index('artworks_user_pack_idx').on(t.userId, t.packId)],
)

export const userProgress = sqliteTable(
  'user_progress',
  {
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    packId: text('pack_id').notNull(),
    totalXp: integer('total_xp').notNull().default(0),
    bonusUnlocked: integer('bonus_unlocked', { mode: 'boolean' }).notNull().default(false),
  },
  (t) => [primaryKey({ columns: [t.userId, t.packId] })],
)

export const answerLog = sqliteTable(
  'answer_log',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    questionId: text('question_id')
      .notNull()
      .references(() => questions.id),
    packId: text('pack_id').notNull(),
    isCorrect: integer('is_correct', { mode: 'boolean' }).notNull(),
    timeTakenMs: integer('time_taken_ms').notNull(),
    answeredAt: integer('answered_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
  },
  (t) => [index('answer_log_user_time_idx').on(t.userId, t.answeredAt)],
)

export const srsState = sqliteTable(
  'srs_state',
  {
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    questionId: text('question_id')
      .notNull()
      .references(() => questions.id),
    packId: text('pack_id').notNull(),
    dueAt: integer('due_at', { mode: 'timestamp_ms' }).notNull(),
    stability: real('stability').notNull(),
    difficulty: real('difficulty').notNull(),
    reps: integer('reps').notNull().default(0),
    lapses: integer('lapses').notNull().default(0),
    state: text('state', { enum: ['new', 'learning', 'review', 'relearning'] })
      .notNull()
      .default('new'),
    lastReview: integer('last_review', { mode: 'timestamp_ms' }),
  },
  (t) => [
    primaryKey({ columns: [t.userId, t.questionId] }),
    index('srs_state_due_idx').on(t.userId, t.dueAt),
  ],
)

export const shares = sqliteTable('shares', {
  // 8-char nanoid for compact share URLs.
  id: text('id').primaryKey(),
  userId: text('user_id')
    .notNull()
    .references(() => users.id),
  artworkId: text('artwork_id')
    .notNull()
    .references(() => userArtworks.id),
  // Canonical R2 key for the composed 1080×1080 share card PNG.
  // Format: `share-cards/{shareId}.png` — derived once at row insert.
  shareCardR2Key: text('share_card_r2_key').notNull(),
  createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
  viewCount: integer('view_count').notNull().default(0),
})

// Step 15 / V-001+V-002+V-005: per-IP request log for the auth endpoints.
// Counted within a sliding window; opportunistic DELETE in middleware keeps
// the table from unbounded growth. See migrations/0004_step15_auth_rate_limit.sql.
export const authRateLimits = sqliteTable(
  'auth_rate_limits',
  {
    id: integer('id').primaryKey({ autoIncrement: true }),
    ip: text('ip').notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (t) => [index('idx_auth_rl_ip_ts').on(t.ip, t.createdAt)],
)

export const SCHEMA_VERSION = '0.1.0-step11'
